import GroupMemberRecord from "models/view-models/group-member-record";
import { useEffect, useState } from "react";
import { CollectionUtils } from "utilities/collection-utils";
import { useGroupContext } from "utilities/contexts/group/group-context-provider";
import useCurrentIdentity from "utilities/hooks/use-current-identity";
import { t } from "utilities/localization-utils";
import GroupMemberService from "utilities/services/groups/group-member-service";
import { ToastManager } from "utilities/toast/toast-manager";

export interface UsePopulateWithExistingTeamMembersOptions {
    onUpdateTeamMembersToMigrate: (groupMembers: GroupMemberRecord[]) => void;
}

const usePopulateWithExistingTeamMembers = ({
    onUpdateTeamMembersToMigrate,
}: UsePopulateWithExistingTeamMembersOptions) => {
    const [areGroupMembersLoading, setAreGroupMembersLoading] = useState(false);
    const [teamOwner, setTeamOwner] = useState<GroupMemberRecord>();
    const { currentIdentity } = useCurrentIdentity();

    const { list: getGroupMembersApi } = GroupMemberService.useList();
    const { groupContext } = useGroupContext();

    useEffect(() => {
        const listGroupMembersApi = async () => {
            const groupId = groupContext?.groupId ?? 0;

            // If new team, don't try to pull team members - there won't be one
            if (groupContext?.isNewTeam || groupId <= 0) {
                setTeamOwner(
                    new GroupMemberRecord().with({
                        firstName: currentIdentity?.user?.firstName,
                        lastName: currentIdentity?.user?.lastName,
                        email: currentIdentity?.user?.email,
                    })
                );
                return;
            }

            try {
                setAreGroupMembersLoading(true);
                const { resultObjects } = await getGroupMembersApi({
                    groupId,
                });
                if (CollectionUtils.isEmpty(resultObjects)) {
                    return;
                }
                setTeamOwner(
                    resultObjects.find(
                        (member) =>
                            member.email === currentIdentity?.user?.email
                    )
                );
                const teamMembersWithoutOwner = resultObjects.filter(
                    (member) => member.email !== currentIdentity?.user?.email
                );
                onUpdateTeamMembersToMigrate(teamMembersWithoutOwner);
            } catch (error) {
                ToastManager.error(
                    t("errors-actionResource", {
                        action: t("loading"),
                        resource: t("team-member_plural"),
                    })
                );
            } finally {
                setAreGroupMembersLoading(false);
            }
        };

        listGroupMembersApi();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return { areGroupMembersLoading, teamOwner };
};

export default usePopulateWithExistingTeamMembers;
